import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Resets the window scroll position on navigation.
 *
 * React Router does not do this for you. It swaps the matched element without
 * touching the document, so the window keeps whatever scroll offset it had —
 * which is why clicking a product halfway down a listing opened the product
 * page already scrolled past its own images and title.
 *
 * Deliberately NOT applied to POP (browser back/forward). Going back to a
 * listing should return you to where you were in it, not to the top; that is
 * the behaviour people expect from a back button, and resetting it makes a long
 * catalogue painful to browse. PUSH and REPLACE are new destinations, so they
 * start at the top.
 *
 * Keyed on pathname only, not the whole location: a changing query string or
 * hash is the same page, and `#refill` in particular is a same-page anchor that
 * must be allowed to do its own scrolling.
 *
 * The page itself is the scroll container — nothing in the app puts `overflow`
 * on html, body or #root — so `window.scrollTo` is the right target. The
 * `scroll-behavior: smooth` rules in home.css apply to the horizontal category
 * and product rails, not to the document, so this lands instantly as intended.
 */
export default function ScrollToTop() {
    const { pathname } = useLocation();
    const navigationType = useNavigationType();

    useEffect(() => {
        if (navigationType === 'POP') return;
        window.scrollTo(0, 0);
    }, [pathname, navigationType]);

    return null;
}
